import type { ScanResponse } from '@pl/domain';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { api } from '../../lib/api';
import { feedback } from '../scanner/feedback';
import { ScanQueue } from './queue';
import type { QueuedScan } from './store';

const QueueContext = createContext<ScanQueue | null>(null);

export interface QueueState {
  /** Scans still waiting to reach the server, in this context when one is given. */
  pending: number;
  /** Scans the server has answered, in this context when one is given. Rises as each one lands. */
  settled: number;
  /** Scans on the wire right now. */
  sending: number;
  attention: QueuedScan[];
  /** False once IndexedDB has refused a write, so the screen stops claiming the phone has the scan. */
  storageOk: boolean;
  /** Bumps on every change; a screen that derives its own counters from the queue depends on it. */
  version: number;
}

/**
 * One queue for the whole scan app, so the header pill, the Receive screen and anything else
 * count the same scans. It sits inside the `/scan` route rather than around the app, because the
 * office runs in a browser tab that has no business holding a phone's offline queue.
 */
export function QueueProvider({ children, queue }: { children: ReactNode; queue?: ScanQueue }) {
  const [instance] = useState(
    () =>
      queue ??
      // §10.4: the phone vibrates when a queued scan is refused. By then the worker has walked
      // away from the screen, so the buzz is the only thing that turns them round.
      new ScanQueue((req) => api.post<ScanResponse>('/scans', req), { onLateRefusal: () => feedback('bad') }),
  );

  useEffect(() => {
    // Whatever the last session left behind goes out before this one starts.
    void instance.hydrate();
    return () => instance.dispose();
  }, [instance]);

  return <QueueContext.Provider value={instance}>{children}</QueueContext.Provider>;
}

export function useQueue(): ScanQueue {
  const queue = useContext(QueueContext);
  if (!queue) throw new Error('useQueue must be used inside <QueueProvider>');
  return queue;
}

export function useQueueState(context?: string): QueueState {
  const queue = useQueue();
  const subscribe = useCallback((fn: () => void) => queue.subscribe(fn), [queue]);
  const snapshot = useCallback(() => queue.version(), [queue]);
  const version = useSyncExternalStore(subscribe, snapshot, snapshot);
  // The version is the only thing React compares; the counts are read fresh whenever it moves.
  return useMemo(
    () => ({ pending: queue.pending(context), settled: queue.settled(context), sending: queue.sending(), attention: queue.attention(), storageOk: queue.storageOk(), version }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queue, context, version],
  );
}
